import { createAsyncActionTypes, lt, showSuccessAlert, Substitutes } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { ApplicationState, AsyncAction, CommanderThunkAction, CommanderThunkDispatch } from '@mews-commander/types';
import { AuthorizationType, CreditCardPaymentParameters, Payment } from '@mews-commander/data-access';
import { createFetchAction, createPrefixer } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { setPayment, UNSPECIFIED_CREDIT_CARD_VALUE } from '@mews-commander/legacy-entities/src/ducks/payments';
import { getPaymentCardById } from '@mews-commander/legacy-entities/src/ducks/paymentCards';
import { fetchPaymentsByIds } from '@mews-commander/legacy-entities/src/actions/payments';
import { isAccountIdCompany } from '@mews-commander/legacy-entities/src/selectors/bills';

const prefix = createPrefixer('payment');

const PAYMENT_ADD = prefix('PAYMENT_ADD');
export const [PAYMENT_ADD_STARTED, PAYMENT_ADD_SUCCEEDED, PAYMENT_ADD_FAILED] = createAsyncActionTypes(PAYMENT_ADD);

const PAYMENT_FETCH = prefix('PAYMENT_FETCH');
export const [PAYMENT_FETCH_STARTED, PAYMENT_FETCH_SUCCEEDED, PAYMENT_FETCH_FAILED] =
    createAsyncActionTypes(PAYMENT_FETCH);

const onSuccess =
    ({
        message: { description, substitutes = {} },
        data,
        accountId,
    }: {
        data: Payment;
        message: {
            description: Mews.TextKey;
            substitutes: Substitutes;
        };
        accountId: string;
    }): CommanderThunkAction =>
    (dispatch, getState, { container }) => {
        const { appLocalizationComponent } = container();
        const isCustomer = !isAccountIdCompany(getState(), { accountId });

        const { localizeCurrencyValue } = appLocalizationComponent;

        showSuccessAlert({
            description: lt(description, {
                ...substitutes,
                amount: localizeCurrencyValue({
                    value: data.amount,
                    currency: data.currency,
                }),
            }),
        });

        if (isCustomer) {
            // @ts-expect-error Argument of type Payment[] is not assignable to parameter of type Payment[]
            dispatch(setPayment(data));
        } else {
            void dispatch(
                fetchPaymentsByIds({
                    ids: [data.id],
                    accountId,
                    extent: { cashiers: true, bills: true, profiles: true },
                })
            );
        }
    };

const createAction =
    (successMessage: Mews.TextKey) =>
    (payload: $TSFixMe): CommanderThunkAction<AsyncAction> =>
    async (dispatch: CommanderThunkDispatch, getState: () => ApplicationState, { container, repositories }) => {
        const { appLocalizationComponent } = container();
        const { parseCurrencyValue } = appLocalizationComponent;
        const enterpriseId = getCurrentEnterpriseId();
        const { amount, creditCardId, accountId, customerId, preauthorizationId, currency, ...other } = payload;

        const cardId = creditCardId !== UNSPECIFIED_CREDIT_CARD_VALUE ? creditCardId : null;

        const parsedPaymentData: CreditCardPaymentParameters = {
            amount: parseCurrencyValue(amount),
            currency,
            creditCardId: cardId,
            preauthorizationId,
            enterpriseId,
            accountId,
            customerId,
            data: preauthorizationId
                ? {
                      discriminator: AuthorizationType.Preauthorization,
                      value: { preauthorizationId },
                  }
                : {
                      discriminator: AuthorizationType.CreditCardPayment,
                      value: {
                          paymentCardId: cardId,
                          currency,
                      },
                  },
            ...other,
        };

        const promise = repositories.creditCardPayments.add(parsedPaymentData).then((data) => {
            const card = data.paymentCardId
                ? getPaymentCardById(getState(), { id: data.paymentCardId, accountId: accountId || customerId })
                : null;

            dispatch(
                onSuccess({
                    message: {
                        description: successMessage,
                        substitutes: { cardLastDigits: card?.lastDigits || '' },
                    },
                    data,
                    accountId: accountId || customerId,
                })
            );

            return data;
        });

        return dispatch(createFetchAction(PAYMENT_ADD, promise));
    };

// only used in billing module
// TODO: move to billing module
export const addCreditCardPayment = createAction(TextKey.CardPaymentSuccessMessage);
